import { useSavedBrokersApi } from "@/Hooks/useSavedBrokersApi";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { UUID } from "@/Shared/types";
import BrokersBox from "@/Shared/Brokers/CustomComponents/BrokersBox";
import BrokersSavedDropdown from "@/Shared/Brokers/Dropdowns/BrokersSavedDropdown";
import { useBrokersAddBrokerDialog } from "@/Features/Brokers/Exports/Hooks/useBrokersAddBrokerDialog";
import { useBrokersModifyBrokerDialog } from "@/Features/Brokers/Exports/Hooks/useBrokersModifyBrokerDialog";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import { useOnce } from "@/Hooks/useOnce";
import type { ReactNode } from "react";
import { useRef, useState } from "react";

interface ViewShipmentsSwitchBrokerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  broker: Broker;
  onFinished: (selectedSavedBrokerId: UUID) => void;
}

function findSavedBrokerId(currentBroker: Broker, savedBrokers: SavedBroker[]) {
  const matchingSavedBroker = savedBrokers.find(function (b) {
    return (
      b.broker.businessName === currentBroker?.businessName &&
      b.broker.address.postalCode === currentBroker.address.postalCode
    );
  });

  return matchingSavedBroker?.savedBrokerId;
}

// Post-booking "Change Customs Broker": pick a saved broker, modify the
// selected one, or add a new one. The add/modify wizards stack above.
function ViewShipmentsSwitchBrokerDialog_(
  props: ViewShipmentsSwitchBrokerDialogProps,
) {
  const [savedBrokers, setSavedBrokers] = useState<Array<SavedBroker>>([]);
  const [loading, setLoading] = useState(false);
  const savedBrokersApi = useSavedBrokersApi();
  const modifyBrokerDialog = useBrokersModifyBrokerDialog();
  const addBrokerDialog = useBrokersAddBrokerDialog();
  const [selectedSavedBrokerId, setSelectedSavedBrokerId] = useState<
    UUID | undefined
  >();

  async function loadSavedBrokers() {
    const savedBrokersResponse = await savedBrokersApi.getAllSavedBrokers();
    setSavedBrokers(savedBrokersResponse.items);

    // First time loading
    if (selectedSavedBrokerId === undefined) {
      setSelectedSavedBrokerId(
        findSavedBrokerId(props.broker, savedBrokersResponse.items),
      );
    }
  }

  useOnce(async function () {
    setLoading(true);
    await loadSavedBrokers();
    setLoading(false);
  });

  async function onAddBroker() {
    const newSavedBroker = await addBrokerDialog.show();
    if (newSavedBroker === undefined) {
      return;
    }
    setLoading(true);
    await savedBrokersApi.addSavedBroker({
      addSavedBrokerRequest: { broker: newSavedBroker },
    });
    await loadSavedBrokers();
    setSelectedSavedBrokerId(newSavedBroker.savedBrokerId);
    setLoading(false);
  }

  const selectedSavedBroker = savedBrokers.find(function (sb) {
    return sb.savedBrokerId === selectedSavedBrokerId;
  });

  function onSwitch(newSavedBrokerId: UUID) {
    setSelectedSavedBrokerId(newSavedBrokerId);
  }

  async function onModify() {
    if (selectedSavedBroker === undefined) {
      return;
    }
    const result = await modifyBrokerDialog.show(selectedSavedBroker);
    if (result !== undefined) {
      await savedBrokersApi.modifySavedBroker({
        modifySavedBrokerRequest: result,
      });
    }
    await loadSavedBrokers();
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title="Change Customs Broker"
        description="Select the customs broker for this shipment"
        width="narrow"
        footer={
          <>
            <Button onClick={() => props.onOpenChange(false)}>Cancel</Button>
            <Button
              primary
              onClick={function () {
                if (selectedSavedBrokerId !== undefined) {
                  props.onFinished(selectedSavedBrokerId);
                  props.onOpenChange(false);
                }
              }}
              disabled={selectedSavedBrokerId === undefined}
              disabledMessage="Select a customs broker"
            >
              Done
            </Button>
          </>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {/* The body used to cap itself at an arbitrary 420px; the width now
              comes from the Dialog's own scale. */}
          <Stack align="start" gap={6}>
            {loading && <Spinner size={32} />}
            <BrokersSavedDropdown
              savedBrokerId={selectedSavedBrokerId}
              savedBrokers={savedBrokers}
              onSetSavedBrokerId={onSwitch}
            />
            {selectedSavedBroker !== undefined && (
              <BrokersBox
                key={selectedSavedBrokerId}
                broker={selectedSavedBroker.broker}
                onModify={onModify}
                showMenu={false}
                onDelete={undefined}
                preferredNorthbound={false}
                preferredSouthbound={false}
                onSelectPreferred={undefined}
              />
            )}
            <Button size="link" onClick={onAddBroker}>
              Add new customs broker
            </Button>
          </Stack>
        </ControlPresentationProvider>
      </Dialog>
      {addBrokerDialog.dialogs}
      {modifyBrokerDialog.dialogs}
    </>
  );
}

// Awaitable wrapper matching the legacy useSwitchBrokerDialog contract:
// resolves the chosen savedBrokerId, or undefined on cancel/close.
export function useSwitchBrokerDialog(): {
  show: (broker: Broker) => Promise<UUID | undefined>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [broker, setBroker] = useState<Broker | undefined>(undefined);
  const resolverRef = useRef<
    ((selectedSavedBrokerId: UUID | undefined) => void) | undefined
  >(undefined);

  function show(currentBroker: Broker): Promise<UUID | undefined> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setBroker(currentBroker);
      setOpenCount((count) => count + 1);
      setOpen(true);
    });
  }

  function settle(selectedSavedBrokerId: UUID | undefined) {
    resolverRef.current?.(selectedSavedBrokerId);
    resolverRef.current = undefined;
  }

  const dialogs = broker !== undefined && (
    <ViewShipmentsSwitchBrokerDialog_
      key={`view-shipments-switch-broker-${openCount}`}
      open={open}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          settle(undefined);
        }
      }}
      broker={broker}
      onFinished={settle}
    />
  );

  return { show, dialogs };
}
